import { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from '../../../utils/fetchAPI';
import { showErrorToast } from '../../../utils/toaster-alert';
import { groupFilesByDate, scopeFilesByVisit } from './helpers';

export function usePatientMedicalFiles(patientId, visitId, filterByVisit = true) {
    const [medicalFiles, setMedicalFiles] = useState([]);
    const [listLoading, setListLoading] = useState(false);

    const loadMedicalFiles = useCallback(async () => {
        if (!patientId) {
            setMedicalFiles([]);
            return;
        }
        setListLoading(true);
        try {
            const res = await get(`patients/${patientId}/medical-files`);
            if (res?.status === 200) {
                setMedicalFiles(res.data?.medicalFiles || []);
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message || err?.message || 'Could not load files.';
            showErrorToast(msg);
            setMedicalFiles([]);
        } finally {
            setListLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        loadMedicalFiles();
    }, [loadMedicalFiles]);

    const scopedFiles = useMemo(() => {
        // Files page is patient-level, so keep records even when visitId is null.
        if (!filterByVisit) return medicalFiles;
        return scopeFilesByVisit(medicalFiles, visitId);
    }, [medicalFiles, visitId, filterByVisit]);

    const groupedByDate = useMemo(
        () => groupFilesByDate(scopedFiles),
        [scopedFiles]
    );

    return {
        scopedFiles,
        groupedByDate,
        listLoading,
        loadMedicalFiles,
    };
}

export function usePatientFilePreview() {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewLoadingId, setPreviewLoadingId] = useState(null);

    const openPreview = useCallback(async (fileId) => {
        setPreviewLoadingId(fileId);
        try {
            // 1) GET /api/files/:fileId — file metadata (existing API)
            const meta = await get(`files/${fileId}`);
            if (meta?.status === 410) {
                showErrorToast(meta?.data?.message || 'File has been deleted.');
                return;
            }
            if (meta?.status !== 200) {
                showErrorToast(meta?.data?.message || 'Could not load file.');
                return;
            }

            // 2) Presigned URL for iframe preview
            const res = await get(`files/${fileId}/signed-url`);
            if (res?.status === 200 && res.data?.url) {
                setPreviewUrl(res.data.url);
                setPreviewOpen(true);
            } else {
                showErrorToast('Could not open file.');
            }
        } catch (err) {
            const status = err?.response?.status;
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Could not open file.';
            if (status === 410) {
                showErrorToast(err?.response?.data?.message || 'File has been deleted.');
            } else {
                showErrorToast(msg);
            }
        } finally {
            setPreviewLoadingId(null);
        }
    }, []);

    const closePreview = useCallback(() => {
        setPreviewOpen(false);
        setPreviewUrl('');
    }, []);

    return {
        previewOpen,
        previewUrl,
        previewLoadingId,
        openPreview,
        closePreview,
    };
}
